// @flow
import Button from '@atlaskit/button';
import DropList, { Item } from '@atlaskit/droplist';
import Pagination from '@atlaskit/pagination';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { usePrevious } from 'react-use';
import { token } from '@atlaskit/tokens';
import usePagination from '../../hooks/usePagination';

const Container = styled.div`
    display: flex;
    align-items: center;
    margin-top: 20px;
    margin-bottom: 20px;
    justify-content: center;
    > .pageSizeOptions {
        padding: 0px 10px;
        display: flex;
        align-items: center;
        font-size: 14px;
        gap: 8px;
    }
`;

export const Text = styled.span`
    color: ${token('color.text')};
`;

const ItemWrapper = styled.div`
    span > span {
        color: ${token('color.text')};
    }

    span > span:hover {
        background-color: ${token('elevation.surface.hovered')};
    }
`;

const PaginationContainer = ({
    rowsCount,
    onPageChange,
    initialPageSize,
}: {
    rowsCount: number,
    onPageChange: (page: number, rowsPerPage: number) => void,
    initialPageSize?: number,
}) => {
    const [displayPageSizeOptions, setDisplayPageSizeOptions] = useState<boolean>(false);
    const togglePageSizeOptionsVisibility = useCallback(() => setDisplayPageSizeOptions((prev) => !prev), []);
    const { pageSize, currentPage, setRowsPerPage, goToPage, pages } = usePagination(rowsCount, initialPageSize, 0);
    const onChangePageSize = useCallback(
        (e) => {
            const rowsPerPage = parseInt(e.target.innerText, 10);
            // This also runs on the first click to open the dropdown, so, to avoid updating unnecessarily, check if the page size actually changed
            if (pageSize !== rowsPerPage) {
                setRowsPerPage(rowsPerPage);
                // Check if the current page is valid for the new page size frame, otherwise, update it to new page size frame last page
                const maxNumberOfPages = Math.ceil(rowsCount / rowsPerPage);
                const newSelectedPage = Math.min(currentPage, Math.max(maxNumberOfPages - 1, 0));
                if (newSelectedPage !== currentPage) {
                    goToPage(newSelectedPage);
                }
                onPageChange(newSelectedPage, rowsPerPage);
            }
            togglePageSizeOptionsVisibility();
        },
        [pageSize, togglePageSizeOptionsVisibility, setRowsPerPage, rowsCount, currentPage, goToPage, onPageChange],
    );

    const onChangePageHandler = useCallback(
        (e, newPage) => {
            goToPage(newPage - 1);
            onPageChange(newPage - 1, pageSize);
        },
        [goToPage, onPageChange, pageSize],
    );

    const prevRowCount = usePrevious(rowsCount);
    useEffect(() => {
        if (prevRowCount !== rowsCount && currentPage !== 0) {
            onChangePageHandler(undefined, 1);
        }
    }, [currentPage, onChangePageHandler, prevRowCount, rowsCount]);

    return (
        <Container>
            <Pagination selectedIndex={currentPage} pages={pages} onChange={onChangePageHandler} />

            <div className="pageSizeOptions">
                <Text>Show</Text>
                <DropList
                    type="option"
                    appearance="default"
                    isTriggerNotTabbable
                    isOpen={displayPageSizeOptions}
                    onClick={onChangePageSize}
                    trigger={<Button>{pageSize}</Button>}
                    testId="droplist"
                >
                    {[5, 10, 15, 20, 25, 50, 100].map((size) => (
                        <ItemWrapper key={size}>
                            <Item key={size} isSelected={pageSize === size}>
                                {size}
                            </Item>
                        </ItemWrapper>
                    ))}
                </DropList>
                <Text>rows per page</Text>
            </div>
        </Container>
    );
};

export default PaginationContainer;
